<script setup>
import {computed, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import createApiService from "@/services/api.js";
import SearchBox from "@/components/SearchBox.vue";
import MaterialCheckViewDialog from "@/components/material/MaterialCheckViewDialog.vue";
import {useFilters} from "@/composables/useFilters.js";

definePage({
  meta: {
    layout: "default",
  },
});

const {t} = useI18n();
const {formatDate, ucFirst} = useFilters();
const route = useRoute();
const router = useRouter();
const api = createApiService(router);

const loading = ref(false);
const search = ref("");
const materialChecks = ref([]);
const showViewDialog = ref(false);
const selectedCheck = ref(null);

const headers = computed(() => [
  {title: t("material.list.headers.project"), value: "project.name", sortable: true},
  {title: t("material.list.headers.employee"), value: "employee.name", sortable: true},
  {title: t("material.list.headers.allChecks"), value: "all_checks_checked", sortable: true, nowrap: true, width: 0},
  {title: t("material.list.headers.differences"), value: "differences_description", sortable: true, nowrap: true, width: 0},
  {title: t("material.list.headers.release"), value: "release_description", sortable: true, nowrap: true, width: 0},
  {title: t("material.list.headers.pdf"), value: "pdf", sortable: true, nowrap: true, width: 0},
  {title: t("material.list.headers.submittedAt"), value: "insertTS", sortable: true, nowrap: true, width: 0},
]);

onMounted(() => {
  updateMaterialChecks();
});

const openMaterialCheckFromQuery = () => {
  const checkId = Number(route.query.checkId ?? 0);
  const projectId = Number(route.query.projectId ?? 0);

  const matchedCheck = checkId
    ? materialChecks.value.find((materialCheck) => Number(materialCheck.id) === checkId)
    : (projectId ? materialChecks.value.find((materialCheck) => Number(materialCheck.project_id) === projectId) : null);

  if (matchedCheck) {
    selectedCheck.value = matchedCheck;
    showViewDialog.value = true;
  }
};

const updateMaterialChecks = async () => {
  loading.value = true;
  try {
    const response = await api.get("materialCheckList");
    materialChecks.value = response.data.data;
    openMaterialCheckFromQuery();
  } catch (error) {
    console.log(error?.response?.data?.data ?? error);
  } finally {
    loading.value = false;
  }
};

const openViewDialog = (event, row) => {
  selectedCheck.value = row.item;
  showViewDialog.value = true;
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const [datePart, timePart = ""] = value.split(" ");
  return `${ucFirst(formatDate(datePart))} ${timePart}`.trim();
};
</script>

<template>
  <v-card>
    <v-card-title class="space-between pt-4 pb-4">
      <span>
        {{ t("material.list.title") }}
        <v-icon icon="mdi-refresh" size="small" color="primary" @click="updateMaterialChecks" />
      </span>
      <div class="d-flex align-center ga-4">
        <search-box v-model="search" />
      </div>
    </v-card-title>

    <v-divider />

    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="materialChecks"
        item-key="id"
        density="compact"
        :loading="loading"
        :search="search"
        class="rowHover"
        :sort-by="[{key: 'insertTS', order: 'desc'}]"
        @click:row="openViewDialog"
      >
        <template #item.insertTS="{ item }">
          {{ formatDateTime(item.insertTS) }}
        </template>

        <template #item.all_checks_checked="{ item }">
          <v-icon :color="item.all_checks_checked ? 'indufastGreen' : 'indufastRed'">
            {{ item.all_checks_checked ? 'mdi-check' : 'mdi-close' }}
          </v-icon>
        </template>

        <template #item.differences_description="{ item }">
          <v-icon :color="item.differences_description === null ? 'indufastGreen' : 'indufastRed'">
            {{ item.differences_description === null ? 'mdi-check' : 'mdi-close' }}
          </v-icon>
        </template>

        <template #item.release_description="{ item }">
          <v-icon :color="item.release_description === null ? 'indufastGreen' : 'indufastRed'">
            {{ item.release_description === null ? 'mdi-check' : 'mdi-close' }}
          </v-icon>
        </template>

        <template #item.pdf="{ item }">
          <v-icon :color="item.google_drive_file_id !== null ? 'indufastGreen' : 'indufastRed'">
            {{ item.google_drive_file_id !== null ? 'mdi-check' : 'mdi-close' }}
          </v-icon>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <material-check-view-dialog
    v-model="showViewDialog"
    :material-check="selectedCheck"
  />
</template>

